import { supabase } from '../supabase';
import { Database } from '../../types/database';

type Quote = Database['public']['Tables']['quotes']['Row'];
type QuoteInsert = Database['public']['Tables']['quotes']['Insert'];
type QuoteUpdate = Database['public']['Tables']['quotes']['Update'];
type QuoteItemInsert = Database['public']['Tables']['quote_items']['Insert'];

export const quoteService = {
  /**
   * Get all quotes with client and items
   */
  getQuotes: async () => {
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        client:clients(*),
        quote_items(*)
      `)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /**
   * Get quote by ID with full details
   */
  getQuoteById: async (id: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        client:clients(*),
        quote_items(*)
      `)
      .eq('id', id)
      .single();
    return { data, error };
  },

  /**
   * Create a new quote
   */
  createQuote: async (quote: Omit<QuoteInsert, 'id' | 'created_at' | 'updated_at' | 'quote_number'>) => {
    const { data, error } = await supabase
      .from('quotes')
      .insert(quote)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Update an existing quote
   */
  updateQuote: async (id: string, updates: QuoteUpdate) => {
    const { data, error } = await supabase
      .from('quotes')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Create quote items
   */
  createQuoteItems: async (items: QuoteItemInsert[]) => {
    // First, delete existing items for this quote if any
    if (items.length > 0 && items[0].quote_id) {
      await supabase
        .from('quote_items')
        .delete()
        .eq('quote_id', items[0].quote_id);
    }

    // Then insert new items
    const { data, error } = await supabase
      .from('quote_items')
      .insert(items)
      .select();
    return { data, error };
  },

  /**
   * Convert quote to invoice
   */
  convertToInvoice: async (quoteId: string, dueDate: string) => {
    // Get quote details
    const { data: quote, error: quoteError } = await supabase
      .from('quotes')
      .select(`
        *,
        quote_items(*)
      `)
      .eq('id', quoteId)
      .single();

    if (quoteError || !quote) {
      return { data: null, error: quoteError };
    }

    // Create invoice
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .insert({
        client_id: quote.client_id,
        subtotal: quote.subtotal,
        tax_rate: quote.tax_rate,
        tax_amount: quote.tax_amount,
        total: quote.total,
        due_date: dueDate,
        notes: `Converted from quote ${quote.quote_number}`,
        created_by: quote.created_by
      })
      .select()
      .single();

    if (invoiceError || !invoice) {
      return { data: null, error: invoiceError };
    }

    // Create invoice items from quote items
    if (quote.quote_items && quote.quote_items.length > 0) {
      const invoiceItems = quote.quote_items.map((item: any) => ({
        invoice_id: invoice.id,
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        amount: item.amount
      }));

      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(invoiceItems);

      if (itemsError) {
        return { data: null, error: itemsError };
      }
    }

    // Update quote status to approved
    await supabase
      .from('quotes')
      .update({ status: 'approved' })
      .eq('id', quoteId);

    return { data: invoice, error: null };
  },

  /**
   * Get quotes by client ID
   */
  getQuotesByClient: async (clientId: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        client:clients(*),
        quote_items(*)
      `)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /**
   * Get quotes by status
   */
  getQuotesByStatus: async (status: 'draft' | 'sent' | 'approved' | 'rejected') => {
    const { data, error } = await supabase
      .from('quotes')
      .select(`
        *,
        client:clients(*),
        quote_items(*)
      `)
      .eq('status', status)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /**
   * Delete a quote
   */
  deleteQuote: async (id: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .delete()
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Send quote (mark as sent)
   */
  sendQuote: async (id: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .update({ status: 'sent' })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Approve quote
   */
  approveQuote: async (id: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .update({ status: 'approved' })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Reject quote
   */
  rejectQuote: async (id: string) => {
    const { data, error } = await supabase
      .from('quotes')
      .update({ status: 'rejected' })
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  }
};